import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { Typography, Paper, useTheme, Box, TextField, Button, Rating, IconButton } from '@mui/material';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';

import { blendColors } from 'lib/helpers';
import { Image } from 'lib/Image';
import { useAuth } from 'lib/useAuth';
import logger from 'lib/logger';
import { isDefinedAndNotNull, isEmptyString } from 'lib/typeInference';
import { ApplicationState } from 'store/store';
import { CheckCircle } from 'ui/CheckCircle';
import { reviewsApi } from './reviewsApi';
import { EReviewSourceType, IReview } from './model/Review';
import { IOrderReadResourceV18 } from './model/Order';

const RATING_GOLD_COLOUR = '#FFCC0C';

interface IReviewPromptProps {
    order: IOrderReadResourceV18;
    review?: IReview | null;
    showSuccessCheckmark?: boolean;
}

export const ReviewPrompt = ({
    order,
    review: existingReview,
    showSuccessCheckmark = true
}: IReviewPromptProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { merchantId } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const backgroundColor = React.useMemo(
        () => blendColors(theme.palette.primary.main, theme.palette.background.default, 0.9),
        [theme]
    );
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { currentLocation } = useSelector((state: ApplicationState) => state.locations);
    const { user } = useAuth();

    const [rating, setRating] = React.useState(0);
    const [review, setReview] = React.useState<IReview | null>(null);
    const [feedback, setFeedback] = React.useState('');
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [isFeedbackEnabled, setIsFeedbackEnabled] = React.useState(false);

    React.useEffect(() => {
        if (!!existingReview) {
            setRating(existingReview.rating);
            setReview(existingReview);
            setFeedback(existingReview.text ?? '');
            setShowSuccess(true);
            setIsFeedbackEnabled(true);
        }
    }, [existingReview]);

    const externalReviewUrl = React.useMemo(() => settings?.reviews?.externalReviewUrl, [settings]);
    const minimumRatingToTriggerExternalReview = React.useMemo(
        () => settings?.reviews?.minimumRatingToTriggerExternalReview,
        [settings]
    );

    const abortController = React.useRef<AbortController | null>(null);

    React.useEffect(
        () => () => {
            if (abortController.current) {
                abortController.current.abort();
            }
        },
        []
    );

    const handleFeedbackChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setFeedback(e.target.value),
        []
    );

    const abortAndCreateSignal = React.useCallback(() => {
        if (abortController.current) {
            abortController.current.abort();
        }
        abortController.current = new AbortController();
        return abortController.current.signal;
    }, []);

    const handleCreateReview = React.useCallback(
        async (newRating: number, text?: string) => {
            if (!merchantId || !user?.id || !order.locationId || newRating <= 0) {
                return;
            }
            const signal = abortAndCreateSignal();
            try {
                const review = await reviewsApi.createReview(
                    {
                        tenantId: merchantId,
                        locationId: order.locationId,
                        orderId: order.id,
                        rating: newRating,
                        text: text ?? undefined,
                        source: {
                            type: EReviewSourceType.WEB_UI_ORDER_CONFIRMATION
                        },
                        userId: user?.id
                    },
                    signal
                );
                if (review) {
                    setReview(review);
                }
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return;
                }
                logger.error('Error creating review', error);
                enqueueSnackbar(t('REVIEWS_ERROR_MESSAGE'), { variant: 'error' });
            }
        },
        [merchantId, user?.id, order.locationId, order.id, abortAndCreateSignal, enqueueSnackbar, t]
    );
    const handleUpdateReview = React.useCallback(
        async (reviewId: string, newRating: number, text?: string) => {
            if (!merchantId || !user?.id || !order.locationId || newRating <= 0) {
                return;
            }
            const signal = abortAndCreateSignal();
            try {
                const updatedReview = await reviewsApi.updateReview(
                    reviewId,
                    {
                        rating: newRating,
                        text: text ?? undefined
                    },
                    signal
                );
                if (updatedReview) {
                    setReview(updatedReview);
                }
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return;
                }
                logger.error('Error updating review', error);
                enqueueSnackbar(t('REVIEWS_ERROR_MESSAGE'), { variant: 'error' });
            }
        },
        [merchantId, user?.id, order.locationId, abortAndCreateSignal, enqueueSnackbar, t]
    );
    const handleOnChange = React.useCallback(
        (_: React.SyntheticEvent, newValue: number | null) => {
            const newRating = newValue ?? 0;
            if (newRating <= 0) {
                return;
            }
            setRating(newRating);
            if (review) {
                handleUpdateReview(review._id, newRating);
            } else {
                handleCreateReview(newRating);
            }
            if (isDefinedAndNotNull(externalReviewUrl) && !isEmptyString(externalReviewUrl)) {
                if (isDefinedAndNotNull(minimumRatingToTriggerExternalReview)) {
                    if (newRating >= minimumRatingToTriggerExternalReview) {
                        window.open(externalReviewUrl, '_blank');
                        setShowSuccess(true);
                    } else {
                        setIsFeedbackEnabled(true);
                    }
                } else {
                    window.open(externalReviewUrl, '_blank');
                    setShowSuccess(true);
                }
            } else {
                setIsFeedbackEnabled(true);
            }
        },
        [
            minimumRatingToTriggerExternalReview,
            externalReviewUrl,
            review,
            handleUpdateReview,
            handleCreateReview
        ]
    );
    const handleSubmit = React.useCallback(() => {
        if (review) {
            handleUpdateReview(review._id, rating, feedback);
        } else {
            handleCreateReview(rating, feedback);
        }
        setShowSuccess(true);
    }, [review, handleUpdateReview, handleCreateReview, feedback, rating]);

    const handleEdit = React.useCallback(() => {
        setShowSuccess(false);
        setIsFeedbackEnabled(true);
    }, []);
    return (
        <Paper sx={{ backgroundColor }} variant="outlined">
            {!showSuccess ? (
                <Box display="flex" flexDirection="column" gap={2} sx={{ py: 8, px: 2 }} alignItems="center">
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <Image
                            src={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/icon_40`}
                            width="40px"
                            height="40px"
                            style={{ borderRadius: '5px' }}
                        />
                        <Box display="flex" flexDirection="column" textAlign="center" gap={1}>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                {t('REVIEWS_TITLE', { location: currentLocation?.title ?? '' })}
                            </Typography>
                            <Typography>{t('REVIEWS_SUBTITLE')}</Typography>
                        </Box>
                    </Box>
                    <Rating
                        name="review-rating"
                        value={rating}
                        onChange={handleOnChange}
                        emptyIcon={
                            <StarOutlinedIcon
                                style={{ opacity: 0.55 }}
                                sx={{ width: '50px', height: '50px' }}
                            />
                        }
                        size="large"
                        icon={
                            <StarOutlinedIcon
                                sx={{ width: '50px', height: '50px', color: RATING_GOLD_COLOUR }}
                            />
                        }
                    />
                    {isFeedbackEnabled && (
                        <>
                            <TextField
                                label={t('REVIEWS_FEEDBACK_HINT')}
                                value={feedback}
                                onChange={handleFeedbackChange}
                                multiline
                                sx={{ width: '100%' }}
                                rows={2}
                            />
                            <Button variant="contained" fullWidth color="primary" onClick={handleSubmit}>
                                {t('REVIEWS_SUBMIT_TITLE')}
                            </Button>
                        </>
                    )}
                </Box>
            ) : (
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    padding={2}
                    alignItems="center"
                    position="relative"
                >
                    {showSuccessCheckmark && <CheckCircle color={theme.palette.primary.main} />}
                    <Typography>{t('REVIEWS_SUCCESS_TITLE')}</Typography>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: showSuccessCheckmark ? 2 : '50%',
                            right: 2,
                            transform: showSuccessCheckmark ? 'none' : 'translateY(-50%)'
                        }}
                        onClick={handleEdit}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
        </Paper>
    );
};
